import React, { useEffect, useState } from 'react';
import { BarChart2 } from 'lucide-react';
import LoaderHelper from '../../utility/Loading/LoaderHelper';
import StockService from '../../Api_Module/Api_Services/StockService';
import ChartAnalysis from '../charts/ChartAnalysis';
import FinancialChart from '../charts/FinancialChart';

interface ChartOverviewProps {
  symbol: any;
}

interface CompanyMetricPair {
  company: string;
  metric: Metric;
  color: string;
}

const ChartOverview: React.FC<ChartOverviewProps> = ({ symbol }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [selectedPairs, setSelectedPairs] = useState<CompanyMetricPair[]>([]);
  const [activeCompany, setActiveCompany] = useState<string>(symbol);
  const [type, setType] = useState('annual');
  const [rangePreset, setRangePreset] = useState<'1Y' | '3Y' | '5Y' | 'ALL'>('5Y');

  // Color palette for company-metric pairs
  const colorPalette = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

  const pickNextColor = (existing: CompanyMetricPair[], pending: CompanyMetricPair[]): string => {
    const used = new Set<string>();
    existing.forEach(p => used.add(p.color));
    pending.forEach(p => used.add(p.color));
    for (let i = 0; i < colorPalette.length; i++) {
      const c = colorPalette[i];
      if (!used.has(c)) return c;
    }
    return colorPalette[(existing.length + pending.length) % colorPalette.length];
  };

  const handlePeriodChange = (period: string) => {
    console.log("Selected Period:", period);
    setType(period);
  };

  useEffect(() => {
    if (!metrics || Object.keys(metrics).length === 0) {
      getAllFinancialMetrics();
    }
  }, [metrics, activeCompany]);

  const onMetricRemove = (pairId: string) => {
    setSelectedPairs(selectedPairs.filter(p => `${p.company}-${p.metric.metricAttributeName}` !== pairId));
  }

  const onSelectCompany = (newCompany: string) => {
    console.log("Selected Company:", newCompany);
    setActiveCompany(newCompany);

    if (!newCompany) return;

    // Get unique metrics that are already selected (from any company)
    const existingMetrics = new Map<string, Metric>();
    selectedPairs.forEach(p => {
      if (!existingMetrics.has(p.metric.metricAttributeName || '')) {
        existingMetrics.set(p.metric.metricAttributeName || '', p.metric);
      }
    });

    // Add the same metrics for the new company
    const newPairs: CompanyMetricPair[] = [];
    existingMetrics.forEach((metric) => {
      // Check if this company-metric pair already exists
      const pairExists = selectedPairs.some(
        p => p.company === newCompany && p.metric.metricAttributeName === metric.metricAttributeName
      );

      if (!pairExists) {
        const color = pickNextColor(selectedPairs, newPairs);
        newPairs.push({
          company: newCompany,
          metric,
          color
        });
      }
    });

    if (newPairs.length > 0) {
      setSelectedPairs([...selectedPairs, ...newPairs]);
    }
  }

  const onMetricSelect = (metric: Metric) => {
    // Get all unique companies that have been selected
    const existingCompanies = new Set<string>();
    selectedPairs.forEach(p => existingCompanies.add(p.company));

    // If no companies selected yet, use activeCompany
    if (existingCompanies.size === 0 && activeCompany) {
      existingCompanies.add(activeCompany);
    }

    // Add this metric for all selected companies
    const newPairs: CompanyMetricPair[] = [];
    existingCompanies.forEach(company => {
      // Check if this company-metric pair already exists
      const pairExists = selectedPairs.some(
        p => p.company === company && p.metric.metricAttributeName === metric.metricAttributeName
      );

      if (!pairExists) {
        const color = pickNextColor(selectedPairs, newPairs);
        newPairs.push({
          company,
          metric,
          color
        });
      }
    });

    if (newPairs.length > 0) {
      setSelectedPairs([...selectedPairs, ...newPairs]);
    }
  }


  // useEffect(() => {
  //   if (metrics.length > 0) {
  //     onMetricSelect(metrics[0]);
  //   }
  // }, [type]);



  const getAllFinancialMetrics = async () => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await StockService.getMetricsKeys(activeCompany);
      LoaderHelper.loaderStatus(false);
      if (result) {
        let metrics: Metric[] = [];
        result.map((metric: any) => {
          metrics.push({
            metricAttributeName: metric.metricAttributeName,
            label: metric.label,
            tooltip: metric.tooltip,
            appendString: metric.appendString,
            appendFormatRepresent: metric.appendFormatRepresent,
            percentage: metric.percentage,
          });
        });
        setMetrics(metrics);
      }
    } catch (error) {
      console.error("Error fetching financial data:", error);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  }

  // Derive from/to based on selected range preset for time-series API
  const computeRange = (): { from?: string; to?: string } => {
    if (rangePreset === 'ALL') {
      return { from: undefined, to: undefined };
    }
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const yearsBack = rangePreset === '1Y' ? 1 : rangePreset === '3Y' ? 3 : 5;
    const fromDate = new Date(Date.UTC(today.getUTCFullYear() - yearsBack, today.getUTCMonth(), today.getUTCDate()));
    const from = fromDate.toISOString().slice(0, 10);
    return { from, to };
  };

  const { from, to } = computeRange();

  return <div className="bg-theme-secondary px-1 hadow-lg overflow-hidden" style={{ margin: '0px' }}>
    <div className="pt-4 px-5 flex justify-between items-center border-theme-tertiary cursor-pointer transition-colors" onClick={() => setIsCollapsed(!isCollapsed)}>
      <div className="flex items-center">
        <BarChart2 size={14} className="text-blue-400 mr-1.5" />
        <h2 className="text-sm font-semibold text-theme-primary">
          Current Financial Metrics
        </h2>
      </div>
    </div>
    <div className="pt-1 px-3">
      <ChartAnalysis
        activeCompany={activeCompany}
        onSelectCompany={onSelectCompany}
        onChangePeriod={handlePeriodChange}
        currentPeriod={type}
        onChangeRange={setRangePreset}
        currentRange={rangePreset}
        onMetricSelect={onMetricSelect}
        onMetricRemove={onMetricRemove}
        selectedPairs={selectedPairs}
        availableMetrics={metrics}
        showChart={false}
      />

      <FinancialChart
        symbols={Array.from(new Set(selectedPairs.map(p => p.company)))}
        companyMetricPairs={selectedPairs}
        period={type as 'annual' | 'quarter'}
        includePrice={true}
        from={from}
        to={to}
      />
    </div>
  </div>;
}
export default ChartOverview;